import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Handshake, Tent, FileText, Backpack, Store, Ticket, Instagram, Megaphone, CalendarDays, Users, LogOut, Sparkles, Mail, Armchair,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const SECTIONS = [
  {
    label: "Workspace",
    items: [
      { to: "/",          label: "Overview", icon: LayoutDashboard, testId: "nav-overview" },
      { to: "/calendar",  label: "Calendar", icon: CalendarDays,    testId: "nav-calendar" },
    ],
  },
  {
    label: "Brand Sales",
    items: [
      { to: "/sponsors",     label: "Sponsors",     icon: Handshake,  testId: "nav-sponsors" },
      { to: "/activations",  label: "Activations",  icon: Tent,       testId: "nav-activations" },
      { to: "/flea-market",  label: "Flea Market",  icon: Store,      testId: "nav-flea-market" },
      { to: "/brand-spaces", label: "Brand Spaces", icon: Backpack,   testId: "nav-brand-spaces" },
      { to: "/deliverables", label: "Deliverables", icon: FileText,   testId: "nav-deliverables" },
      { to: "/outreach",     label: "Outreach",     icon: Mail,       testId: "nav-outreach" },
    ],
  },
  {
    label: "Tickets & Tables",
    items: [
      { to: "/tickets", label: "Tickets",    icon: Ticket,   testId: "nav-tickets" },
      { to: "/tables",  label: "VIP Tables", icon: Armchair, testId: "nav-tables" },
    ],
  },
  {
    label: "Social Media",
    items: [
      { to: "/instagram", label: "Instagram", icon: Instagram, testId: "nav-instagram" },
      { to: "/ads",       label: "Ads",       icon: Megaphone, testId: "nav-ads" },
    ],
  },
];

const navItemCls = ({ isActive }) =>
  `group flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg border-l-2 transition-colors ${
    isActive
      ? "border-emerald-500 bg-white/[0.06] text-white"
      : "border-transparent text-zinc-400 hover:text-white hover:bg-white/[0.03]"
  }`;

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  return (
    <aside
      className="fixed left-0 top-0 bottom-0 w-64 bg-[#0a0f1e]/80 backdrop-blur-xl border-r border-white/5 flex flex-col z-30"
      data-testid="sidebar"
    >
      <div className="px-6 pt-7 pb-6 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 grid place-items-center bg-emerald-500 text-black rounded-md">
            <Sparkles className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display font-bold text-[15px] tracking-tight">ISF OPS</div>
            <div className="overline mt-1.5">Delhi · Dec 19–20</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {SECTIONS.map((section, idx) => (
          <div key={section.label} className={idx > 0 ? "mt-5" : ""}>
            <div className="overline px-3 mb-2">{section.label}</div>
            <div className="space-y-0.5">
              {section.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testId}
                  className={navItemCls}
                >
                  <n.icon className="h-4 w-4" strokeWidth={1.75} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
        {isAdmin && (
          <div className="mt-5">
            <div className="overline px-3 mb-2">Admin</div>
            <NavLink to="/team" data-testid="nav-team" className={navItemCls}>
              <Users className="h-4 w-4" strokeWidth={1.75} />
              <span>Team</span>
            </NavLink>
          </div>
        )}
      </nav>

      <div className="border-t border-white/5 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-8 w-8 grid place-items-center bg-white/[0.04] border border-white/10 text-emerald-400 font-display font-semibold text-sm rounded-md">
            {user?.name?.[0]?.toUpperCase() ?? "?"}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
            <div className="overline mt-0.5">{user?.role}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="w-full inline-flex items-center justify-center gap-2 py-2 text-xs uppercase tracking-[0.2em] border border-white/10 text-zinc-300 hover:bg-white/[0.04] hover:text-white transition-colors rounded-md"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
